/**
 * Sprint P32 · Block Model layer.
 * GPU-instanced cubes with per-block position + scale + colour, rendered
 * in true world scale. Includes the crisp Surpac/Leapfrog edge overlay.
 */
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { worldToScene, isValidCoordinate } from "@/lib/sceneCoords";
import {
  CLASS_HEX, PASS_HEX, ORE_HEX, LITHO_HEX, hashHex, pickColour,
} from "../utils/colorPalettes";


export function BlockMesh({
  blocks, palette, cutoff, showOre, showWaste, slicer, slicerPos,
  origin, defaultBlockSize, verticalExaggeration = 1,
  zRange = null, classSet = null, domainSet = null, minSamples = 0,
  colourBy = "grade",
  onHoverBlock, onSelectBlock,
  onBounds,   // (Box3, {rendered, rejected, invalid}) → void
}) {
  const meshRef = useRef();
  const visibleIdxRef = useRef([]);

  const { grades, count, unitCubeSize, oreMask } = useMemo(() => {
    const gs = blocks.map((b) => Number(b.grade ?? b.g ?? 0));
    // Split ore/waste by the reporting cut-off. Then percentile-rank the
    // ore grades so the full spectral palette is used regardless of
    // absolute grade magnitude — matches the Leapfrog rainbow look even
    // for low-grade exploration projects.
    const oreVals = gs.filter((g) => g >= cutoff && g > 0)
                       .sort((a, b) => a - b);
    const orePct = new Map();
    oreVals.forEach((g, i) => orePct.set(g, (i + 1) / oreVals.length));
    const oreM = gs.map((g) => g >= cutoff && g > 0);
    return {
      grades:      gs.map((g) => (g >= cutoff && g > 0 ? orePct.get(g) ?? 0.5 : 0)),
      oreMask:     oreM,
      count:       blocks.length,
      unitCubeSize: 1,
    };
  }, [blocks, cutoff]);

  useEffect(() => {
    if (!meshRef.current || !blocks.length || !origin) return;
    const dummy = new THREE.Object3D();
    const color = new THREE.Color();
    const localBox = new THREE.Box3();
    const halfLo = new THREE.Vector3();
    const halfHi = new THREE.Vector3();
    let idx = 0;
    let rejected = 0;
    let invalid = 0;
    const visibleIdx = [];

    const [bx, by, bz] = defaultBlockSize || [10, 10, 5];

    blocks.forEach((b, i) => {
      if (!isValidCoordinate(b.x, b.y, b.z)) { invalid++; return; }
      const g = Number(b.grade ?? b.g ?? 0);
      const isOre = g >= cutoff;
      if ((isOre && !showOre) || (!isOre && !showWaste)) { rejected++; return; }
      if (slicer === "ew" && Math.abs(b.y - slicerPos) > by * 2) { rejected++; return; }
      if (slicer === "ns" && Math.abs(b.x - slicerPos) > bx * 2) { rejected++; return; }
      if (zRange && (b.z < zRange[0] || b.z > zRange[1])) { rejected++; return; }
      if (classSet && classSet.size && !classSet.has(b.classification || "Unclassified")) { rejected++; return; }
      if (domainSet && domainSet.size && b.domain && !domainSet.has(b.domain)) { rejected++; return; }
      if (minSamples > 0 && Number(b.n_samples || 0) < minSamples) { rejected++; return; }

      const pos = worldToScene(b.x, b.y, b.z, origin, verticalExaggeration);
      // Per-block dimensions: prefer block-level dx/dy/dz, fall back to summary.
      const dx = Number(b.dx || b.bx || bx);
      const dy = Number(b.dy || b.by || by);
      const dz = Number(b.dz || b.bz || bz) * verticalExaggeration;
      dummy.position.copy(pos);
      dummy.scale.set(dx, dz, dy);   // note: scene Y=elev, scene Z=-north
      dummy.rotation.set(0, 0, 0);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(idx, dummy.matrix);

      halfLo.set(pos.x - dx / 2, pos.y - dz / 2, pos.z - dy / 2);
      halfHi.set(pos.x + dx / 2, pos.y + dz / 2, pos.z + dy / 2);
      localBox.expandByPoint(halfLo);
      localBox.expandByPoint(halfHi);

      let hex;
      if (colourBy === "classification") hex = CLASS_HEX[b.classification || "Unclassified"];
      else if (colourBy === "pass") hex = PASS_HEX[Number(b.used_pass || 0)];
      else if (colourBy === "domain") {
        const seed = String(b.domain || "0").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
        hex = palette[seed % palette.length];
      } else if (colourBy === "lithology") {
        const lith = String(b.lithology || b.rock || "").toUpperCase();
        hex = LITHO_HEX[lith] || (lith ? hashHex(lith) : "#94a3b8");
      } else if (colourBy === "ore_waste") {
        hex = isOre ? ORE_HEX.ore : ORE_HEX.waste;
      } else {
        // Grade colouring · full spectral rainbow across every visible
        // block, waste in muted slate so the ore stands out clearly.
        hex = oreMask[i] ? pickColour(grades[i], palette) : "#334155";
      }

      color.set(hex);
      meshRef.current.setColorAt(idx, color);
      visibleIdx.push(i);
      idx++;
    });
    meshRef.current.count = idx;
    meshRef.current.instanceMatrix.needsUpdate = true;
    if (meshRef.current.instanceColor) meshRef.current.instanceColor.needsUpdate = true;
    meshRef.current.computeBoundingBox?.();
    meshRef.current.computeBoundingSphere?.();
    meshRef.current.frustumCulled = idx > 0;
    visibleIdxRef.current = visibleIdx;

    onBounds?.(localBox, { rendered: idx, rejected, invalid });
  }, [blocks, palette, cutoff, showOre, showWaste, grades, slicer, slicerPos,
        origin, defaultBlockSize, verticalExaggeration,
        zRange, classSet, domainSet, minSamples, colourBy, oreMask, onBounds]);

  const handlePointerMove = (e) => {
    if (!onHoverBlock || e.instanceId === undefined || e.instanceId === null) return;
    const realIdx = visibleIdxRef.current[e.instanceId];
    if (realIdx === undefined) return;
    onHoverBlock(blocks[realIdx], e.clientX, e.clientY);
  };
  const handlePointerOut = () => onHoverBlock?.(null);
  const handleClick = (e) => {
    if (e.instanceId === undefined || e.instanceId === null) return;
    const realIdx = visibleIdxRef.current[e.instanceId];
    if (realIdx === undefined) return;
    e.stopPropagation();
    onSelectBlock?.(blocks[realIdx]);
  };

  if (!count) return null;
  return (
    <instancedMesh ref={meshRef}
                   args={[undefined, undefined, count]}
                   onPointerMove={handlePointerMove}
                   onPointerOut={handlePointerOut}
                   onClick={handleClick}
                   renderOrder={1}
                   userData={{ coordinateSpace: "local_scene", layer: "blocks" }}>
      {/* Filled instanced cubes — real block dimensions from the block
          model (dx × dy × dz). Faces are opaque so palette colours stay
          pure. No wireframe overlay — a wireframe drawn at 1.001× scale
          leaks between adjacent blocks and produces a fake ghost halo
          instead of showing the true model shape. Individual blocks are
          identifiable via the built-in `lineWidth` of the geometry
          normals when the block colours differ; when a whole domain is
          one lithology (e.g. GRN), the model correctly renders as a
          solid volume — that is the honest geological picture, not a
          wireframe illusion of detail. */}
      <boxGeometry args={[unitCubeSize, unitCubeSize, unitCubeSize]} />
      <meshLambertMaterial toneMapped={false} />
    </instancedMesh>
  );
}
